import type { PracticeSession } from '../types/index.js';

/** Sessions shorter than this are still saved but do not count toward the practice streak. */
export const MIN_STREAK_SESSION_SECONDS = 60;

export function isValidTimeZone(tz?: string): boolean {
  if (!tz || typeof tz !== 'string') return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function calculateTehranStats(
  sessions: PracticeSession[],
  routineBlockMap: Map<string, string>,
  nowDate: Date = new Date(),
  timeZone: string = typeof Intl !== 'undefined' ? (Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Tehran') : 'Asia/Tehran'
) {
    const safeTimeZone = isValidTimeZone(timeZone) ? timeZone : 'Asia/Tehran';
    const now = nowDate;
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: safeTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const weekdayFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: safeTimeZone,
      weekday: 'short'
    });

    const getTehranDateStr = (d: Date): string => formatter.format(d);

    const durationByDate = new Map<string, number>();
    const activeByDate = new Map<string, number>();
    const streakByDate = new Map<string, number>();

    let highestCleanBpm = 0;
    let highestCleanBpmBlock: {
      bpm: number;
      category?: string;
      routineTitle?: string;
      blockTitle?: string;
      date: string;
    } | null = null;

    for (const s of sessions) {
      const d = new Date(s.createdAt || now.toISOString());
      const dateStr = getTehranDateStr(d);
      durationByDate.set(dateStr, (durationByDate.get(dateStr) || 0) + (s.totalDurationSeconds || 0));
      // Sessions shorter than the minimum are saved but never count toward the streak.
      if ((s.totalDurationSeconds || 0) >= MIN_STREAK_SESSION_SECONDS) {
        streakByDate.set(dateStr, (streakByDate.get(dateStr) || 0) + (s.totalDurationSeconds || 0));
      }
      activeByDate.set(dateStr, (activeByDate.get(dateStr) || 0) + (s.activePlaybackSeconds || 0));

      for (const b of s.blocks || []) {
        if (b.achievedBpm && b.achievedBpm > highestCleanBpm) {
          highestCleanBpm = b.achievedBpm;
          highestCleanBpmBlock = {
            bpm: b.achievedBpm,
            category: b.category,
            routineTitle: routineBlockMap.get(b.blockId) || 'روتین تمرینی',
            blockTitle: undefined,
            date: s.createdAt || now.toISOString()
          };
        }
      }
    }

    const dayMap: Record<string, number> = { Sat: 0, Sun: 1, Mon: 2, Tue: 3, Wed: 4, Thu: 5, Fri: 6 };
    const currentDayIndex = dayMap[weekdayFormatter.format(now)] ?? 0;
    const saturday = new Date(now.getTime() - currentDayIndex * 86400000);

    const week: Array<{ date: string; seconds: number }> = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(saturday.getTime() + i * 86400000);
      const dateStr = getTehranDateStr(day);
      week.push({
        date: dateStr,
        seconds: durationByDate.get(dateStr) || 0
      });
    }

    // Streak days
    const todayStr = getTehranDateStr(now);
    let streakDays = 0;
    let checkDate = (streakByDate.get(todayStr) || 0) > 0 ? now : new Date(now.getTime() - 86400000);

    if ((streakByDate.get(getTehranDateStr(checkDate)) || 0) > 0) {
      while ((streakByDate.get(getTehranDateStr(checkDate)) || 0) > 0) {
        streakDays++;
        checkDate = new Date(checkDate.getTime() - 86400000);
      }
    }

    // This week & last week seconds
    let thisWeekSeconds = 0;
    let thisWeekActiveSeconds = 0;
    for (let i = 0; i <= currentDayIndex; i++) {
      const dStr = getTehranDateStr(new Date(now.getTime() - i * 86400000));
      thisWeekSeconds += durationByDate.get(dStr) || 0;
      thisWeekActiveSeconds += activeByDate.get(dStr) || 0;
    }

    let lastWeekSeconds = 0;
    for (let i = currentDayIndex + 1; i <= currentDayIndex + 7; i++) {
      const dStr = getTehranDateStr(new Date(now.getTime() - i * 86400000));
      lastWeekSeconds += durationByDate.get(dStr) || 0;
    }

    const weekOverWeekPercent =
      lastWeekSeconds > 0
        ? Math.round(((thisWeekSeconds - lastWeekSeconds) / lastWeekSeconds) * 100)
        : 0;

    return {
      week,
      currentDayIndex,
      streakDays,
      thisWeekSeconds,
      lastWeekSeconds,
      thisWeekActiveSeconds,
      weekOverWeekPercent,
      highestCleanBpm,
      highestCleanBpmBlock
    };
}
